import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Mail, MapPin, Store } from 'lucide-react';

import InquiryForm from '../../components/public/InquiryForm';
import PublicLayout from '../../components/public/PublicLayout';
import { Card } from '../../components/ui/card';
import { business } from '../../content/siteContent.mjs';

const Contact = () => (
  <PublicLayout testId="public-contact">
    <section className="pt-16 pb-14 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto text-center">
        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-amber-600">Get in touch</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          Contact MAD Donuts
        </h1>
        <p className="text-lg text-gray-600 leading-relaxed">
          Wholesale, catering, a question about a flavour or an allergen — send it here and a human replies
          within one business day.
        </p>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Send a message</h2>
          <InquiryForm variant="general" testId="contact-form" />
        </div>

        <div className="lg:col-span-2 space-y-5">
          <Card className="p-6">
            <div className="mb-3 inline-flex rounded-full bg-amber-100 p-3">
              <Mail size={20} className="text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Email</h3>
            <a
              href={`mailto:${business.email}`}
              className="text-base font-medium text-amber-700 hover:text-amber-800"
              data-testid="contact-email-link"
            >
              {business.email}
            </a>
          </Card>

          <Card className="p-6">
            <div className="mb-3 inline-flex rounded-full bg-amber-100 p-3">
              <MapPin size={20} className="text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Where we bake</h3>
            <p className="text-base text-gray-600">{business.addressLine}</p>
            <p className="mt-2 text-sm text-gray-500">
              Wholesale and catering delivery only — there is no walk-in counter. To buy a single donut, see{' '}
              <Link to="/where-to-find-us" className="font-semibold text-amber-700 hover:text-amber-800">
                where to find us
              </Link>
              .
            </p>
          </Card>

          <Card className="p-6">
            <div className="mb-3 inline-flex rounded-full bg-amber-100 p-3">
              <Clock size={20} className="text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Ordering deadlines</h3>
            <p className="text-base text-gray-600">
              Wholesale orders close {business.leadTime} before the delivery morning. Catering is best booked a
              week ahead for large runs.
            </p>
          </Card>

          <Card className="p-6">
            <div className="mb-3 inline-flex rounded-full bg-amber-100 p-3">
              <Store size={20} className="text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Already a partner?</h3>
            <p className="text-base text-gray-600 mb-3">
              Standing orders, invoices and delivery photos live in your portal.
            </p>
            <a
              href="/client/login"
              className="text-base font-semibold text-amber-700 hover:text-amber-800"
              data-testid="contact-client-login"
            >
              Client login →
            </a>
          </Card>
        </div>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-amber-50/60">
      <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Card className="p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Supplying a café or restaurant</h2>
          <p className="text-sm text-gray-600 mb-4">
            The wholesale form asks for the few details we need to price and route your deliveries.
          </p>
          <Link to="/wholesale#inquiry" className="font-semibold text-amber-700 hover:text-amber-800">
            Wholesale inquiry →
          </Link>
        </Card>
        <Card className="p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-2">One-off event or office order</h2>
          <p className="text-sm text-gray-600 mb-4">
            Pick a package, tell us the date and the town, and we will quote it.
          </p>
          <Link to="/catering#quote" className="font-semibold text-amber-700 hover:text-amber-800">
            Catering quote →
          </Link>
        </Card>
      </div>
    </section>
  </PublicLayout>
);

export default Contact;
